import { LegacyPortalSearchServiceSearchGoodsResponse } from "@spwn-portal/grpc";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";

import { managePageNumber } from "../managePageNumber";
import { useSearchedCondition } from "../useSearchedCondition";
import { convertResponse } from "./functions/convertResponse";

import type { GoodsSearchCondition } from "../../types/goodsSearchCondition";
import type { PromiseClient } from "@bufbuild/connect";
import type { LegacyPortalSearchService } from "@spwn-portal/grpc";
import type { HostingType } from "@spwn/types/firebase/firestore";
import type { MutationFetcher } from "swr/mutation";

type Input = {
  belongedHosting: HostingType;
  client: PromiseClient<typeof LegacyPortalSearchService>;
};

const SWR_KEY = "searchGoods";
const { getPage, incrementPage, resetPage } = managePageNumber();

export const useGoodsSearch = (input: Input) => {
  const { belongedHosting, client } = input;
  const [hasGoodsSearched, searchedCondition, setSearchedCondition] =
    useSearchedCondition<"goods">();
  const { data } = useSWR<LegacyPortalSearchServiceSearchGoodsResponse>(
    SWR_KEY,
    null,
    { revalidateOnMount: false }
  );
  const fetcher: MutationFetcher<
    LegacyPortalSearchServiceSearchGoodsResponse,
    string,
    GoodsSearchCondition
  > = async (_, { arg }) => {
    const { pageNumber, ...rest } = arg;
    const searchCondition = {
      pageNumber,
      ...rest,
      hostingType: belongedHosting,
    } as const;
    const result = client.searchGoods(searchCondition);
    setSearchedCondition({ ...searchCondition, type: "goods" });
    return result;
  };
  const { isMutating, trigger: goodsTrigger } = useSWRMutation(
    SWR_KEY,
    fetcher
  );
  const goodsResultCount = data?.totalCount ?? 0;
  const goodsSearchResults = data?.items.flatMap(convertResponse) ?? [];
  const handleSearchGoods = (condition: GoodsSearchCondition) => {
    const { keyword } = condition;
    resetPage();
    // 非同期関数じゃない。await すると実行時に runtime error が出る
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    goodsTrigger(
      {
        keyword,
      },
      {
        populateCache: true,
      }
    );
  };
  const handleLoadMoreGoodsList = () => {
    if (searchedCondition === null) return;
    incrementPage();
    const { type: _, ...condition } = searchedCondition;

    // 非同期関数じゃない。await すると実行時に runtime error が出る
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    goodsTrigger(
      {
        ...condition,
        pageNumber: getPage(),
      },
      {
        populateCache: (newData, currentData) => {
          const base =
            currentData?.clone() ??
            new LegacyPortalSearchServiceSearchGoodsResponse({
              items: [],
              totalCount: 0,
            });
          base.items.push(...newData.items);
          base.totalCount = newData.totalCount ?? base.totalCount;
          return base;
        },
        revalidate: false,
        rollbackOnError: true,
      }
    );
  };

  return {
    goodsResultCount,
    goodsSearchResults,
    handleLoadMoreGoodsList,
    handleSearchGoods,
    hasGoodsSearched,
    isLoading: isMutating && data === undefined,
    isMoreLoading: isMutating,
  };
};
